import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Plus, X } from 'lucide-react';
import { SALUTATIONS, TITLES, GENDERS, MONTHS_DE } from './constants';
import { FieldLabel, CustomFields, usePlzCities, CitySuggestions, PhoneField, VerifyCodeBlock } from './shared';
import { cityKeptOrFilled } from '@/lib/plzOrt';
import { isValidPhone, cleanPhoneInput } from './utils';

const PHONE_TYPES = ['Festnetz', 'Mobil', 'Geschäftlich', 'Sonstige'];

export default function StepPayer({ form, setField, setCustom, setRoot, F, title, hint, customFields, invalid, tenantSlug, phoneRequired = false }) {
  const p = form.payer;
  const show = (k) => F(k).visible;
  const ec = (k) => (invalid(k) ? 'border-red-500 focus-visible:ring-red-500 bg-red-50/50' : '');
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => String(currentYear - 16 - i));
  const days = Array.from({ length: 31 }, (_, i) => String(i + 1));

  const plzCities = usePlzCities(p.postal_code);
  useEffect(() => {
    const next = cityKeptOrFilled(p.city, plzCities);
    if (next !== (p.city ?? '')) setField('payer', 'city', next);
    // setField kommt aus dem Wizard und ändert sich mit dem Formular.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plzCities, p.city]);

  return (
    <div className="space-y-5" data-testid="public-registration-step-payer">
      <h2 className="text-lg md:text-lg font-semibold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500">{hint}</p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {show('payer.salutation') && (
          <div>
            <FieldLabel spec={F('payer.salutation')} />
            <Select value={p.salutation} onValueChange={(v) => setField('payer', 'salutation', v)}>
              <SelectTrigger data-testid="public-registration-payer-salutation"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {SALUTATIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {show('payer.title') && (
          <div>
            <FieldLabel spec={F('payer.title')} />
            <Select value={p.title} onValueChange={(v) => setField('payer', 'title', v)}>
              <SelectTrigger data-testid="public-registration-payer-title"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
              <SelectContent>
                {TITLES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <FieldLabel spec={F('payer.first_name')} error={invalid('payer.first_name')} />
          <Input placeholder="Max" className={ec('payer.first_name')} value={p.first_name} onChange={(e) => setField('payer', 'first_name', e.target.value)} data-testid="public-registration-payer-firstname" />
        </div>
        <div>
          <FieldLabel spec={F('payer.last_name')} error={invalid('payer.last_name')} />
          <Input placeholder="Mustermann" className={ec('payer.last_name')} value={p.last_name} onChange={(e) => setField('payer', 'last_name', e.target.value)} data-testid="public-registration-payer-lastname" />
        </div>
      </div>

      {(show('payer.street') || show('payer.house_number') || show('payer.address_extra')) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {show('payer.street') && (
            <div className="md:col-span-2">
              <FieldLabel spec={F('payer.street')} error={invalid('payer.street')} />
              <Input placeholder="Musterstr." className={ec('payer.street')} value={p.street} onChange={(e) => setField('payer', 'street', e.target.value)} data-testid="public-registration-payer-street" />
            </div>
          )}
          {show('payer.house_number') && (
            <div>
              <FieldLabel spec={F('payer.house_number')} error={invalid('payer.house_number')} />
              <Input placeholder="221" inputMode="numeric" className={ec('payer.house_number')} value={p.house_number} onChange={(e) => setField('payer', 'house_number', e.target.value.replace(/\D/g, ''))} data-testid="public-registration-payer-housenumber" />
            </div>
          )}
          {show('payer.address_extra') && (
            <div>
              <FieldLabel spec={F('payer.address_extra')} />
              <Input placeholder="b" value={p.address_extra} onChange={(e) => setField('payer', 'address_extra', e.target.value)} data-testid="public-registration-payer-addressextra" />
            </div>
          )}
        </div>
      )}

      {(show('payer.postal_code') || show('payer.city') || show('payer.district')) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {show('payer.postal_code') && (
            <div>
              <FieldLabel spec={F('payer.postal_code')} error={invalid('payer.postal_code')} />
              <Input placeholder="PLZ" inputMode="numeric" maxLength={5} className={ec('payer.postal_code')} value={p.postal_code} onChange={(e) => setField('payer', 'postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))} data-testid="public-registration-payer-postal" />
            </div>
          )}
          {show('payer.city') && (
            <div className="md:col-span-2">
              <FieldLabel spec={F('payer.city')} error={invalid('payer.city')} />
              <Input placeholder="Wohnort" className={ec('payer.city')} value={p.city} onChange={(e) => setField('payer', 'city', e.target.value)} data-testid="public-registration-payer-city" />
              <CitySuggestions cities={plzCities} current={p.city} onPick={(c) => setField('payer', 'city', c)} testPrefix="public-registration-payer" />
            </div>
          )}
          {show('payer.district') && (
            <div>
              <FieldLabel spec={F('payer.district')} />
              <Input value={p.district} onChange={(e) => setField('payer', 'district', e.target.value)} data-testid="public-registration-payer-district" />
            </div>
          )}
        </div>
      )}

      {(show('payer.gender') || show('payer.birthdate')) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {show('payer.gender') && (
            <div>
              <FieldLabel spec={F('payer.gender')} />
              <Select value={p.gender} onValueChange={(v) => setField('payer', 'gender', v)}>
                <SelectTrigger data-testid="public-registration-payer-gender"><SelectValue placeholder="Bitte wählen…" /></SelectTrigger>
                <SelectContent>
                  {GENDERS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {show('payer.birthdate') && (
            <>
              <div>
                <Label>Geburtstag</Label>
                <Select value={p.birth_day} onValueChange={(v) => setField('payer', 'birth_day', v)}>
                  <SelectTrigger data-testid="public-registration-payer-birthday"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {days.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Monat</Label>
                <Select value={p.birth_month} onValueChange={(v) => setField('payer', 'birth_month', v)}>
                  <SelectTrigger data-testid="public-registration-payer-birthmonth"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {MONTHS_DE.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jahr</Label>
                <Select value={p.birth_year} onValueChange={(v) => setField('payer', 'birth_year', v)}>
                  <SelectTrigger data-testid="public-registration-payer-birthyear"><SelectValue placeholder="--" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel spec={F('payer.email')} error={invalid('payer.email')} />
          <Input type="email" placeholder="max.mustermann@beispiel.de" className={ec('payer.email')} value={p.email} onChange={(e) => setField('payer', 'email', e.target.value)} data-testid="public-registration-payer-email" />
        </div>
        {show('payer.phone') && (
          <div>
            <FieldLabel spec={{ ...F('payer.phone'), label: 'Mobilfunknummer' }} error={invalid('payer.phone')} />
            <PhoneField value={p.phone} onChange={(v) => setField('payer', 'phone', v)} error={invalid('payer.phone')} testId="public-registration-payer-phone" />
            {phoneRequired && (
            <VerifyCodeBlock
              tenantSlug={tenantSlug}
              kind="phone"
              value={p.phone}
              valueOk={isValidPhone(p.phone)}
              verification={form.phone_verification}
              setVerification={(v) => setRoot('phone_verification', v)}
              requiredError={invalid('payer.phone_verify')}
            />
            )}
            <div className="mt-3 space-y-2">
              {(p.additional_phones || []).map((ap, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`public-registration-payer-extra-phone-row-${idx}`}>
                  <Select
                    value={ap.type}
                    onValueChange={(v) => {
                      const arr = [...p.additional_phones];
                      arr[idx] = { ...arr[idx], type: v };
                      setField('payer', 'additional_phones', arr);
                    }}
                  >
                    <SelectTrigger className="w-36 shrink-0" data-testid={`public-registration-payer-extra-phone-type-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PHONE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="0441 123456"
                    inputMode="tel"
                    value={ap.number}
                    onChange={(e) => {
                      const arr = [...p.additional_phones];
                      arr[idx] = { ...arr[idx], number: cleanPhoneInput(e.target.value) };
                      setField('payer', 'additional_phones', arr);
                    }}
                    data-testid={`public-registration-payer-extra-phone-number-${idx}`}
                  />
                  <button
                    type="button"
                    className="text-slate-400 hover:text-rose-600 shrink-0"
                    onClick={() => setField('payer', 'additional_phones', p.additional_phones.filter((_, i) => i !== idx))}
                    title="Nummer entfernen"
                    data-testid={`public-registration-payer-extra-phone-remove-${idx}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              {(p.additional_phones || []).length < 5 && (
                <button
                  type="button"
                  className="text-sm text-brand-red hover:text-brand-red font-medium inline-flex items-center gap-1"
                  onClick={() => setField('payer', 'additional_phones', [...(p.additional_phones || []), { type: 'Festnetz', number: '' }])}
                  data-testid="public-registration-payer-add-phone-btn"
                >
                  <Plus className="h-4 w-4" /> Nummer hinzufügen
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <CustomFields customFields={customFields} form={form} setCustom={setCustom} invalid={invalid} testPrefix="public-registration-payer" />
    </div>
  );
}
